import React from 'react';
// AI Requests - firm-wide "AI ideas" board on the /requests portal. Submit an
// idea, upvote, comment. Rendered inside the portal card (RequestsPortal
// provides the header + Back link). Access is gated server-side to an allowlist
// (see AI_REQUESTS_ALLOWED in functions/src/index.js); the tile that opens this
// is only shown to the same person. All text renders via React children, so
// user content is auto-escaped.

const { useState, useEffect, useCallback, useRef } = React;

const aiApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'aiRequests');

// Palette matches the portal (warm neutrals + Triage teal).
const C = {
  accent: '#0D9488', accentDk: '#0F766E', accentBg: '#ECFDF9',
  border: '#E7E5E2', text: '#1F2937', muted: '#6B7280', faint: '#9CA3AF',
  card: '#FFFFFF', bg: '#F6F5F3', radius: 6,
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Keep in sync with AI_CATEGORIES / AI_STATUSES in functions/src/index.js
// (the backend validates against those).
const CATEGORIES = ['Legal Drafting', 'Legal Research', 'Legal Admin', 'Business Services', 'General'];
const STATUSES = [
  { id: 'open',      label: 'New',          color: '#0F766E', bg: '#ECFDF9' },
  { id: 'reviewing', label: 'Under review', color: '#92400E', bg: '#FFFBEB' },
  { id: 'planned',   label: 'Planned',      color: '#3730A3', bg: '#EEF2FF' },
  { id: 'building',  label: 'Building',     color: '#1E40AF', bg: '#EFF6FF' },
  { id: 'done',      label: 'Done',         color: '#065F46', bg: '#ECFDF5' },
  { id: 'declined',  label: 'Declined',     color: '#991B1B', bg: '#FEF2F2' },
];
const statusMeta = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

export function AiRequestsBoard({ isAdmin = false }) {
  const [ideas, setIdeas] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc]   = useState('');
  const [cat, setCat]     = useState('General');
  const [submitting, setSubmitting] = useState(false);
  const [sort, setSort]   = useState('top');      // 'top' | 'new'
  const [catFilter, setCatFilter] = useState(''); // '' = all categories
  const [showForm, setShowForm] = useState(false); // submit popup open?

  const load = useCallback(async () => {
    try { setIdeas(await aiApi('GET', '/ai-requests') || []); setError(null); }
    catch (e) { setError(e.message || 'Failed to load'); setIdeas([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || submitting) return;
    setSubmitting(true);
    try {
      await aiApi('POST', '/ai-requests', { title: t, description: desc.trim() || null, category: cat });
      setTitle(''); setDesc('');
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Submit failed: ' + (err.message || 'try again'));
    } finally {
      setSubmitting(false);
    }
  };

  const vote = async (idea) => {
    // Optimistic toggle, reconciled with the server's authoritative count.
    setIdeas((list) => list.map((i) => i.id === idea.id
      ? { ...i, hasVoted: !i.hasVoted, voteCount: i.voteCount + (i.hasVoted ? -1 : 1) } : i));
    try {
      const r = await aiApi('POST', `/ai-requests/${idea.id}/vote`);
      setIdeas((list) => list.map((i) => i.id === idea.id
        ? { ...i, voteCount: r.voteCount, hasVoted: r.hasVoted } : i));
    } catch (_) { load(); }
  };

  const addComment = async (idea, body) => {
    const c = await aiApi('POST', `/ai-requests/${idea.id}/comments`, { body });
    setIdeas((list) => list.map((i) => i.id === idea.id
      ? { ...i, comments: [...(i.comments || []), c] } : i));
  };

  const setStatus = async (idea, status) => {
    setIdeas((list) => list.map((i) => i.id === idea.id ? { ...i, status } : i));
    try { await aiApi('PATCH', `/ai-requests/${idea.id}`, { status }); }
    catch (_) { load(); }
  };

  const deleteIdea = async (idea) => {
    if (!window.confirm(`Delete "${idea.title}"?\n\nThis also removes its votes and comments and can't be undone.`)) return;
    setIdeas((list) => list.filter((i) => i.id !== idea.id));
    try { await aiApi('DELETE', `/ai-requests/${idea.id}`); }
    catch (e) { alert('Delete failed: ' + (e.message || 'try again')); load(); }
  };

  const sorted = (ideas || [])
    .filter((i) => !catFilter || i.category === catFilter)
    .slice()
    .sort((a, b) => sort === 'new'
      ? (new Date(b.createdAt) - new Date(a.createdAt))
      : ((b.voteCount - a.voteCount) || (new Date(b.createdAt) - new Date(a.createdAt))));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: C.font, color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ fontSize: 21, fontWeight: 650, margin: 0, letterSpacing: '-0.01em' }}>AI Ideas</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0', lineHeight: 1.5 }}>
            Got an idea for how AI could help at the firm? Submit it here, and upvote or comment on others' ideas.
          </p>
        </div>
        <button type="button" onClick={() => setShowForm(true)} style={btnPrimary}>+ Submit an idea</button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 6, color: '#991B1B', fontSize: 13 }}>
          {error}
        </div>
      )}

      {Array.isArray(ideas) && ideas.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: -4 }}>
          <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
            {[['top', 'Top'], ['new', 'Newest']].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setSort(id)}
                style={{
                  padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: sort === id ? C.accentBg : '#fff', color: sort === id ? C.accentDk : C.muted, fontFamily: C.font,
                }}>
                {label}
              </button>
            ))}
          </div>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: 13 }}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {ideas === null ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: C.faint, fontSize: 13 }}>Loading ideas…</div>
      ) : sorted.length === 0 ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: C.faint, fontSize: 13 }}>
          {(ideas && ideas.length > 0) ? 'No ideas in this category.' : 'No ideas yet. Be the first to add one.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map((idea) => (
            <IdeaCard key={idea.id} idea={idea} isAdmin={isAdmin}
              onVote={() => vote(idea)}
              onComment={(body) => addComment(idea, body)}
              onStatus={(s) => setStatus(idea, s)}
              onDelete={() => deleteIdea(idea)}/>
          ))}
        </div>
      )}

      {showForm && (
        <div onClick={() => !submitting && setShowForm(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            style={{ background: C.card, borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.22)', width: 640, maxWidth: '100%', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 14, fontFamily: C.font }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, fontWeight: 650, color: C.text }}>Submit an AI idea</div>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 18, color: C.muted, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
            </div>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
              placeholder="Your idea in a sentence" maxLength={300} style={inputStyle}/>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="Optional: a bit more detail. What would it do, and who'd use it?" rows={6}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 130 }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, color: C.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                Category
                <select value={cat} onChange={(e) => setCat(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '7px 10px' }}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ padding: '9px 16px', background: '#fff', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 14, cursor: 'pointer', fontFamily: C.font }}>
                  Cancel
                </button>
                <button type="submit" disabled={!title.trim() || submitting}
                  style={{ ...btnPrimary, ...(!title.trim() || submitting ? { background: C.faint, cursor: 'not-allowed' } : {}) }}>
                  {submitting ? 'Submitting…' : 'Submit idea'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Solid status pill + click-to-change dropdown. Cleaner than a native <select>
// (which sizes to the widest option and reads like a half-empty input box).
function StatusControl({ status, onChange, editable = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const sm = statusMeta(status);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  // Non-admins see a static pill (no dropdown). Hooks above still run.
  if (!editable) {
    return (
      <span style={{
        fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 999,
        color: '#fff', background: sm.color, fontFamily: C.font, whiteSpace: 'nowrap',
      }}>{sm.label}</span>
    );
  }
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} title="Set status"
        style={{
          fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
          color: '#fff', background: sm.color, border: 'none', fontFamily: C.font,
          display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
        }}>
        {sm.label}<span style={{ fontSize: 8, opacity: 0.85 }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4, minWidth: 150,
          background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 30, overflow: 'hidden',
        }}>
          {STATUSES.map((s) => (
            <button key={s.id} type="button" onClick={() => { onChange(s.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', cursor: 'pointer', fontFamily: C.font,
                fontSize: 12.5, color: C.text, background: s.id === status ? '#F6F5F3' : '#fff',
              }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.color, flexShrink: 0 }}/>
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function IdeaCard({ idea, onVote, onComment, onStatus, onDelete, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const comments = idea.comments || [];
  // Keep long descriptions from cluttering the board - clamp and let the reader
  // expand. Character-based so we don't need to measure the DOM.
  const descText = idea.description || '';
  const DESC_LIMIT = 220;
  const descLong = descText.length > DESC_LIMIT;
  const descShown = (descLong && !descOpen) ? descText.slice(0, DESC_LIMIT).trimEnd() + '…' : descText;

  const post = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    try { await onComment(body); setDraft(''); }
    catch (e) { alert('Comment failed: ' + (e.message || 'try again')); }
    finally { setPosting(false); }
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.card, padding: '16px 18px', display: 'flex', gap: 16 }}>
      {/* Vote button */}
      <button type="button" onClick={onVote} title={idea.hasVoted ? 'Remove your vote' : 'Upvote'}
        style={{
          flexShrink: 0, width: 52, minHeight: 60, borderRadius: 10, cursor: 'pointer',
          border: `1px solid ${idea.hasVoted ? C.accent : C.border}`,
          background: idea.hasVoted ? C.accentBg : '#fff',
          color: idea.hasVoted ? C.accentDk : C.muted,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
          fontFamily: C.font, transition: 'border-color 120ms, background 120ms',
        }}>
        <span style={{ fontSize: 13, lineHeight: 1 }}>▲</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{idea.voteCount}</span>
      </button>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title left, category + status pushed right so the card fills its width. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, lineHeight: 1.35 }}>{idea.title}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {idea.category && (
              <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, background: '#F1EFEA', padding: '4px 10px', borderRadius: 999 }}>
                {idea.category}
              </span>
            )}
            <StatusControl status={idea.status} onChange={onStatus} editable={isAdmin}/>
          </div>
        </div>
        {descText && (
          <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {descShown}
            {descLong && (
              <button type="button" onClick={() => setDescOpen((o) => !o)}
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, cursor: 'pointer', color: C.accentDk, fontFamily: C.font, fontSize: 12, fontWeight: 600 }}>
                {descOpen ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}
        <div style={{ fontSize: 12, color: C.faint, marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{idea.submittedByName} · {fmtWhen(idea.createdAt)}</span>
          <button type="button" onClick={() => setOpen((o) => !o)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.muted, fontFamily: C.font, fontSize: 11.5 }}>
            💬 {comments.length} comment{comments.length !== 1 ? 's' : ''}{open ? ' ▲' : ' ▼'}
          </button>
          {isAdmin && (
            <button type="button" onClick={onDelete} title="Delete idea"
              style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#B91C1C', fontFamily: C.font, fontSize: 12 }}>
              Delete
            </button>
          )}
        </div>

        {open && (
          <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {comments.length === 0 && <div style={{ fontSize: 12, color: C.faint, fontStyle: 'italic' }}>No comments yet.</div>}
            {comments.map((c) => (
              <div key={c.id} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: C.text }}>{c.authorName}</span>
                <span style={{ color: C.faint, fontSize: 11, marginLeft: 6 }}>{fmtWhen(c.createdAt)}</span>
                <div style={{ color: C.text, marginTop: 1, whiteSpace: 'pre-wrap' }}>{c.body}</div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
              <input
                type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') post(); }}
                placeholder="Add a comment…"
                style={{ ...inputStyle, flex: 1, padding: '6px 10px' }}
              />
              <button type="button" onClick={post} disabled={!draft.trim() || posting}
                style={{ ...btnPrimary, padding: '6px 12px', ...(!draft.trim() || posting ? { background: C.faint, cursor: 'not-allowed' } : {}) }}>
                Post
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '9px 12px', border: `1px solid ${C.border}`, borderRadius: 6,
  fontSize: 14, color: C.text, background: '#fff', outline: 'none',
  fontFamily: C.font, width: '100%', boxSizing: 'border-box',
};
const btnPrimary = {
  padding: '9px 18px', background: C.accent, color: '#fff', border: 'none',
  borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: C.font,
};
